import React, {ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {intersperseNodes, RendererOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';

/*
 * Styles.
 */

const StyledMetaSpan = styled.span<{$noPaddingRight?: boolean; $isMedium?: boolean}>`
  ${(p) => !p.$noPaddingRight && 'padding-right: 5px;'}
  ${(p) => p.$isMedium && `font-weight: ${fontWeights.medium};`}
`;

/*
 * Constants.
 */

const CommaSeparator = (
  <StyledMetaSpan>
    <Trans id="filter-dropdown-separator">,</Trans>
  </StyledMetaSpan>
);

const ExtraItemsRenderer = (count: number) => (
  <StyledMetaSpan $noPaddingRight $isMedium>
    , +{count}
  </StyledMetaSpan>
);

/*
 * Helpers.
 */

interface RenderDropdownValueOptions {
  maxItems?: number;
  separator?: ReactNode;
  extraItemsRenderer?: RendererOf<number>;
}

/**
 * Render a list of items, separated by `separator` in a dropdown value.
 * If the items exceed `maxItems`, only the first `maxItems` items will be rendered.
 *
 * @param items
 * @param renderer
 * @param maxItems
 * @param separator
 * @param extraItemsRenderer
 */
export function renderDropdownValueItems<T extends {key: string}>(
  items: ReadonlyArray<T>,
  renderer: RendererOf<T>,
  {
    maxItems = 3,
    separator = CommaSeparator,
    extraItemsRenderer = ExtraItemsRenderer,
  }: RenderDropdownValueOptions = {},
) {
  const itemsToRender = items.slice(0, maxItems);
  const extraItemsCount = items.slice(maxItems).length;
  const renderedItems = itemsToRender.map((item) => ({key: item.key, node: renderer(item)}));

  return (
    <>
      {intersperseNodes(renderedItems, () => separator)}
      {extraItemsCount > 0 ? extraItemsRenderer(extraItemsCount) : null}
    </>
  );
}
